import { useId, useState } from "react";
import { styled } from "styled-components";
import { RichText } from "./RichText.js";
import { minus, plus } from "../icons/index.js";
import { mobileBreakPointCSS } from "../defaults/theme.js";
import { css } from "styled-components";
import { surfaceColorMap } from "../util/styles.js";
import type { BackgroundColor } from "../types/theme.js";

interface Props {
  title: string;
  text: string;
  backgroundColor?: BackgroundColor;
}

export const ExpandableSection: React.FC<Props> = ({ title, text, backgroundColor }) => {
  const id = useId();
  const [active, setActive] = useState<boolean>(false);

  return (
    <Wrapper className="expandable-section" $bg={backgroundColor}>
      <SectionHeader
        className="section-header"
        role="button"
        aria-expanded={active}
        aria-controls={id}
        onClick={() => setActive(!active)}
      >
        <Title className="b2">{title}</Title>
        {active ? minus : plus}
      </SectionHeader>

      {active && text && (
        <SectionBody id={id} className="section-body">
          <RichText dangerouslySetInnerHTML={{ __html: text }} />
        </SectionBody>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $bg?: BackgroundColor }>`
  ${(p) =>
    p.$bg
      ? css`
          background-color: ${surfaceColorMap[p.$bg]};
          color: ${p.$bg === "surface3" ? "#fff" : "var(--swatch2)"};
          svg path {
            fill: currentColor;
            stroke: currentColor;
          }
        `
      : css`
          background-color: var(--expandable-background);
          color: var(--expandable-text-color);
          svg path {
            fill: var(--expandable-icon-color, var(--primary-icon-color));
          }
        `}
  border-radius: ${(props) => props.theme.borderRadius};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.theme.spacing.s};
  gap: ${(props) => props.theme.spacing.s};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${(props) => props.theme.spacing.m};
  }
`;

const Title = styled.h3`
  margin: 0;
  flex: 1;
`;

const SectionBody = styled.div`
  padding: ${(props) =>
    `0 ${props.theme.spacing.s} ${props.theme.spacing.s} ${props.theme.spacing.s}`};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${(props) =>
      `0 ${props.theme.spacing.m} ${props.theme.spacing.m} ${props.theme.spacing.m}`};
  }
`;
